/**
 * The risks a plan holds, as one list (roadmap N-4).
 *
 * A plan's risks live in two tables. People write them in the plan's narrative
 * (`por_risks`, through RisksPanel and `createRisk`), reached from the plan cycle
 * by `por_records.plan_cycle_id`. The public API writes them against a plan
 * version (`risks`, POST /v1/plan-cycles/:id/versions/:vid/risks), and the
 * planning engine scores those. The Assumptions & Risks page shows both, so it
 * shows every risk that exists on the plan. This module reads and never writes:
 * adding a risk stays with `createRisk`.
 */

import type { PorRisk, PorRiskSeverity, PorRiskStatus } from '../types/plan-of-record';
import type { Risk } from '../types/planning';
import { assemblePor, getPorForCycle } from './planOfRecordService';
import { getSupabaseClient } from './supabaseClient';

export interface PlanRiskRow {
  id: string;
  description: string;
  /** Set on a risk from the narrative. */
  severity: PorRiskSeverity | null;
  /** Set on a risk from the plan version. */
  impact: number | null;
  likelihood: number | null;
  mitigation: string | null;
  status: PorRiskStatus | null;
}

/** The narrative's risks in their own order, then the plan version's. */
export function planRiskRows(narrativeRisks: readonly PorRisk[], versionRisks: readonly Risk[]): PlanRiskRow[] {
  return [
    ...narrativeRisks.map((r) => ({
      id: r.id,
      description: r.description,
      severity: r.severity,
      impact: null,
      likelihood: null,
      mitigation: r.mitigation,
      status: r.status,
    })),
    ...versionRisks.map((r) => ({
      id: r.id,
      description: r.description,
      severity: null,
      impact: r.impact_score,
      likelihood: r.likelihood_score,
      mitigation: r.mitigation_plan,
      status: null,
    })),
  ];
}

/** Every risk on a plan cycle: the linked narrative's, and the plan version's when one is named. */
export async function fetchPlanRisks(
  cycleId: string,
  planVersionId: string | null,
): Promise<{ rows: PlanRiskRow[]; error: string | null }> {
  const [narrative, version] = await Promise.all([
    getPorForCycle(cycleId),
    planVersionId
      ? getSupabaseClient().from('risks').select('*').eq('plan_version_id', planVersionId)
      : Promise.resolve({ data: [] as Risk[], error: null }),
  ]);

  let narrativeRisks: PorRisk[] = [];
  let error = narrative.error ?? version.error?.message ?? null;
  if (narrative.data) {
    const assembled = await assemblePor(narrative.data.id);
    if (assembled.error) error = assembled.error;
    else if (assembled.data?.success) narrativeRisks = assembled.data.risks;
    else error = assembled.data?.error ?? 'The plan narrative could not be read.';
  }
  return { rows: planRiskRows(narrativeRisks, (version.data ?? []) as Risk[]), error };
}
